import React, { useState, useEffect } from 'react';
import {
  Mail, Phone, MapPin, Clock, ArrowRight, Send,
  CheckCircle, AlertCircle, Shield, Zap, Target, TrendingUp,
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import { sendContactEmail } from '../../utils/emailService';
import Reveal from '../../components/enterprise/Reveal';
import SectionHead from '../../components/enterprise/SectionHead';
import '../../styles/enterprise.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  // 'idle' | 'success' | 'error' — presentation only; submission logic is unchanged.
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    document.body.classList.add('ibm-plex-page');
    return () => document.body.classList.remove('ibm-plex-page');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (status !== 'idle') setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await sendContactEmail(formData);
      setIsSubmitting(false);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setIsSubmitting(false);
      setStatus('error');
      console.error('Form submission error:', error);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'info@axonmetric.com',
      href: 'mailto:info@axonmetric.com',
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91-7827355185',
      href: 'tel:+917827355185',
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'India',
    },
    {
      icon: Clock,
      label: 'Business hours',
      value: 'Mon-Fri: 9:00 AM - 6:00 PM',
    },
  ];

  const features = [
    {
      icon: Shield,
      title: '24/7 support',
      description: 'Round-the-clock technical assistance.',
    },
    {
      icon: Zap,
      title: 'Fast response',
      description: 'Quick turnaround on all inquiries.',
    },
    {
      icon: Target,
      title: 'Expert guidance',
      description: 'Industry-leading renewable energy expertise.',
    },
    {
      icon: TrendingUp,
      title: 'Proven results',
      description: 'Track record of successful implementations.',
    },
  ];

  const fields = [
    { id: 'name', label: 'Full name', type: 'text', placeholder: 'Jane Doe', required: true, half: true },
    { id: 'email', label: 'Email address', type: 'email', placeholder: 'jane@company.com', required: true, half: true },
    { id: 'company', label: 'Company', type: 'text', placeholder: 'Your company', half: true },
    { id: 'phone', label: 'Phone number', type: 'tel', placeholder: '+91-7827355185', half: true },
    { id: 'subject', label: 'Subject', type: 'text', placeholder: 'What is this regarding?', required: true },
  ];

  return (
    <Layout header={6} footer={6}>
      <main className="ax">
        {/* --- Hero --------------------------------------------------------- */}
        <section className="ax-hero">
          <div className="ax-hero__bg">
            <img src="/assets/img/general/energy-2302001_1280.jpg" alt="" />
          </div>
          <div className="ax-hero__scrim" />
          <div className="ax-hero__inner" style={{ paddingBlock: 'clamp(88px, 9vw, 144px)' }}>
            <div className="ax-container">
              <div className="ax-hero__copy">
                <Reveal>
                  <span className="ax-eyebrow">Get in touch</span>
                </Reveal>
                <Reveal delay={60}>
                  <h1 className="ax-display">
                    Let&rsquo;s start a <em>conversation</em>
                  </h1>
                </Reveal>
                <Reveal delay={120}>
                  <p className="ax-hero__lede">
                    Ready to transform your renewable energy operations? Get in touch with our
                    team of experts to discuss how we can help optimize your assets and maximize
                    performance.
                  </p>
                </Reveal>
                <Reveal delay={180} className="ax-hero__actions">
                  <a href="#contact-form" className="ax-btn ax-btn--invert">
                    <span>Send a message</span>
                    <ArrowRight className="ax-btn__arrow" size={17} />
                  </a>
                  <a href="tel:+917827355185" className="ax-btn ax-btn--ghost-invert">
                    <Phone size={17} />
                    <span>Call now</span>
                  </a>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* --- Form + contact information ----------------------------------- */}
        <section className="ax-section" id="contact-form">
          <div className="ax-container">
            <div
              className="ax-split"
              style={{ gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)', alignItems: 'start' }}
            >
              {/* Form */}
              <Reveal>
                <span className="ax-eyebrow">Send us a message</span>
                <h2 className="ax-h2" style={{ marginTop: 16 }}>
                  Tell us what you need
                </h2>
                <p className="ax-lede" style={{ marginTop: 16, marginBottom: 40 }}>
                  Fill out the form and our team will get back to you within 24 hours.
                </p>

                {status === 'success' && (
                  <div
                    role="status"
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: 16,
                      marginBottom: 28,
                      border: '1px solid var(--ax-blue-200)',
                      background: 'var(--ax-blue-50)',
                      borderRadius: 'var(--ax-radius)',
                    }}
                  >
                    <CheckCircle size={18} style={{ flex: 'none', color: 'var(--ax-blue-600)' }} />
                    <p className="ax-body" style={{ color: 'var(--ax-blue-700)' }}>
                      Thank you for your message. We will get back to you soon.
                    </p>
                  </div>
                )}
                {status === 'error' && (
                  <div
                    role="alert"
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: 16,
                      marginBottom: 28,
                      border: '1px solid #F2C3BF',
                      background: '#FDF3F2',
                      borderRadius: 'var(--ax-radius)',
                    }}
                  >
                    <AlertCircle size={18} style={{ flex: 'none', color: '#B3261E' }} />
                    <p className="ax-body" style={{ color: '#7A1C16' }}>
                      There was an error sending your message. Please try again, or contact us
                      directly at <a href="mailto:info@axonmetric.com">info@axonmetric.com</a>.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 24 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 24,
                    }}
                  >
                    {fields.filter((f) => f.half).map((field) => (
                      <div className="ax-field" key={field.id}>
                        <label htmlFor={field.id}>
                          {field.label}
                          {field.required && <span aria-hidden="true"> *</span>}
                        </label>
                        <input
                          type={field.type}
                          id={field.id}
                          name={field.id}
                          value={formData[field.id]}
                          onChange={handleInputChange}
                          required={field.required}
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>

                  {fields.filter((f) => !f.half).map((field) => (
                    <div className="ax-field" key={field.id}>
                      <label htmlFor={field.id}>
                        {field.label}
                        {field.required && <span aria-hidden="true"> *</span>}
                      </label>
                      <input
                        type={field.type}
                        id={field.id}
                        name={field.id}
                        value={formData[field.id]}
                        onChange={handleInputChange}
                        required={field.required}
                        placeholder={field.placeholder}
                      />
                    </div>
                  ))}

                  <div className="ax-field">
                    <label htmlFor="message">
                      Message<span aria-hidden="true"> *</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      placeholder="Tell us about your project or inquiry..."
                    />
                    <p className="ax-field__hint">Fields marked * are required.</p>
                  </div>

                  <div>
                    <button type="submit" disabled={isSubmitting} className="ax-btn ax-btn--primary">
                      {isSubmitting ? (
                        <>
                          <Send size={17} className="ax-spin" />
                          <span>Sending message…</span>
                        </>
                      ) : (
                        <>
                          <span>Send message</span>
                          <ArrowRight className="ax-btn__arrow" size={17} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </Reveal>

              {/* Contact information */}
              <Reveal delay={100}>
                <div className="ax-panel">
                  <h2 className="ax-panel__title">Contact information</h2>
                  <dl style={{ display: 'grid', gap: 0, margin: 0 }}>
                    {contactInfo.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.label}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '20px minmax(0, 1fr)',
                            gap: 16,
                            alignItems: 'start',
                            paddingBlock: 20,
                            borderTop: i === 0 ? 0 : '1px solid var(--ax-border)',
                          }}
                        >
                          <Icon size={20} style={{ color: 'var(--ax-blue-500)', marginTop: 2 }} />
                          <div>
                            <dt className="ax-small" style={{ marginBottom: 4 }}>{item.label}</dt>
                            <dd style={{ margin: 0 }}>
                              {item.href ? (
                                <a
                                  href={item.href}
                                  className="ax-h4"
                                  style={{ color: 'var(--ax-blue-700)', textDecoration: 'none' }}
                                >
                                  {item.value}
                                </a>
                              ) : (
                                <span className="ax-h4">{item.value}</span>
                              )}
                            </dd>
                          </div>
                        </div>
                      );
                    })}
                  </dl>
                </div>

                <figure className="ax-media ax-media--wide" style={{ marginTop: 24 }}>
                  <img
                    src="/assets/img/general/steptodown.com799996.jpg"
                    alt="Renewable energy operations supported by AxonMetric"
                  />
                  <figcaption className="ax-media__caption">
                    <p className="ax-h4" style={{ color: '#fff' }}>
                      Let&rsquo;s build the future together
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            </div>
          </div>
        </section>

        {/* --- Why work with us --------------------------------------------- */}
        <section className="ax-section ax-section--alt">
          <div className="ax-container">
            <SectionHead
              eyebrow="Why choose us"
              title="Professional excellence"
              lede="Experience the difference with our industry-leading renewable energy solutions."
            />
            <div className="ax-grid ax-grid--4 ax-grid--hairline" style={{ marginTop: 56 }}>
              {features.map((feature, i) => {
                const Icon = feature.icon;
                return (
                  <Reveal key={feature.title} className="ax-card" delay={i * 70}>
                    <span className="ax-card__icon">
                      <Icon />
                    </span>
                    <h3 className="ax-h3">{feature.title}</h3>
                    <p className="ax-body">{feature.description}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* --- Closing CTA --------------------------------------------------- */}
        <section className="ax-section ax-section--ink">
          <div className="ax-container">
            <div className="ax-split" style={{ alignItems: 'center' }}>
              <Reveal>
                <span className="ax-eyebrow">Next step</span>
                <h2 className="ax-h2" style={{ marginTop: 16 }}>
                  Ready to transform your energy operations?
                </h2>
                <p className="ax-lede" style={{ marginTop: 16 }}>
                  Let&rsquo;s discuss how our advanced analytics and monitoring solutions can
                  optimize your renewable energy assets.
                </p>
              </Reveal>
              <Reveal delay={80} style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <a href="#contact-form" className="ax-btn ax-btn--invert">
                  <span>Schedule a demo</span>
                  <ArrowRight className="ax-btn__arrow" size={17} />
                </a>
                <a href="tel:+917827355185" className="ax-btn ax-btn--ghost-invert">
                  <Phone size={17} />
                  <span>Call now</span>
                </a>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default ContactPage;
